import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";

const Integrations = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isStaff = role === "admin" || role === "moderator";
    const isAdmin = role === "admin";

    const [data, setData] = useState(null);
    const [status, setStatus] = useState(null);
    const [forms, setForms] = useState({});
    const [test, setTest] = useState({});

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    const load = () => {
        axios.get("/api/integrations").then((res) => {
            setData(res.data.data);
            setStatus(res.data.status);
            const f = {};
            Object.entries(res.data.data).forEach(([name, v]) => { f[name] = { enabled: v.enabled, config: { ...v.config } }; });
            setForms(f);
        }).catch(() => {});
    };

    useEffect(() => { if (isStaff) load(); }, [isStaff]);

    if (!isStaff) {
        return <div className="page-content-main-container"><h2 className="page-title">{t("intg.title")}</h2></div>;
    }

    const setField = (name, key, value) => setForms((p) => ({ ...p, [name]: { ...p[name], config: { ...p[name].config, [key]: value } } }));
    const setEnabled = (name, value) => setForms((p) => ({ ...p, [name]: { ...p[name], enabled: value } }));

    const save = (name) => {
        axios.put(`/api/integrations/${name}`, forms[name])
            .then((res) => { notify(res.data.message.en, "success-notification"); load(); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const runTest = (name) => {
        if (name === "auction") {
            axios.post("/api/integrations/auction/fetch", { lot: test.lot, vin: test.vin })
                .then((res) => setTest((p) => ({ ...p, auctionResult: res.data.data }))).catch(() => {});
        } else if (name === "carrier") {
            axios.post("/api/integrations/carrier/track", { tracking: test.tracking })
                .then((res) => setTest((p) => ({ ...p, carrierResult: res.data.data }))).catch(() => {});
        }
    };

    const badge = (ok) => (
        <span style={{ padding: "2px 10px", borderRadius: "12px", fontSize: "12px", background: ok ? "#34c38f" : "#6c757d", color: "#fff" }}>
            {ok ? t("intg.configured") : t("intg.notConfigured")}
        </span>
    );
    const txt = (name, key, label, secretSet) => (
        <div style={{ marginBottom: "10px" }}>
            <label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{label} {secretSet && <span style={{ opacity: 0.5 }}>{t("intg.secretSet")}</span>}</label>
            <input className="form-control" value={forms[name].config[key] || ""} disabled={!isAdmin} onChange={(e) => setField(name, key, e.target.value)} />
        </div>
    );
    const card = (name, title, body, extra) => (
        <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", marginBottom: "16px", maxWidth: "620px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                <h3 style={{ fontSize: "15px", margin: 0 }}>{title}</h3>
                {status && badge(status[name])}
            </div>
            {body}
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", margin: "6px 0 14px" }}>
                <input type="checkbox" checked={!!forms[name].enabled} disabled={!isAdmin} onChange={(e) => setEnabled(name, e.target.checked)} /> {t("intg.enabled")}
            </label>
            {isAdmin && <button className="btn btn-sm btn-primary" onClick={() => save(name)}>{t("intg.save")}</button>}
            {extra}
        </div>
    );

    if (!data || !forms.auction) return <div className="page-content-main-container"><h2 className="page-title">{t("intg.title")}</h2></div>;

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("intg.title")}</h2>
            <div style={{ fontSize: "13px", opacity: 0.7, marginBottom: "18px" }}>{t("intg.hint")}</div>

            {card("auction", t("intg.auction"), <>
                {txt("auction", "base_url", t("intg.baseUrl"))}
                {txt("auction", "api_key", t("intg.apiKey"), data.auction.config.has_api_key)}
            </>, (
                <div style={{ marginTop: "14px", borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div><label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("intg.lot")}</label><input className="form-control" style={{ width: "140px" }} value={test.lot || ""} onChange={(e) => setTest((p) => ({ ...p, lot: e.target.value }))} /></div>
                        <div><label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("intg.vin")}</label><input className="form-control" style={{ width: "180px" }} value={test.vin || ""} onChange={(e) => setTest((p) => ({ ...p, vin: e.target.value }))} /></div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => runTest("auction")}>{t("intg.fetch")}</button>
                    </div>
                    {test.auctionResult && <pre style={{ fontSize: "11px", background: "var(--surface-2, rgba(127,127,127,0.1))", padding: "10px", borderRadius: "8px", marginTop: "10px", overflowX: "auto" }}>{JSON.stringify(test.auctionResult, null, 2)}</pre>}
                </div>
            ))}

            {card("carrier", t("intg.carrier"), <>
                {txt("carrier", "base_url", t("intg.baseUrl"))}
                {txt("carrier", "api_key", t("intg.apiKey"), data.carrier.config.has_api_key)}
            </>, (
                <div style={{ marginTop: "14px", borderTop: "1px solid var(--border)", paddingTop: "12px" }}>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div><label style={{ fontSize: "12px", opacity: 0.7, display: "block" }}>{t("intg.tracking")}</label><input className="form-control" style={{ width: "200px" }} value={test.tracking || ""} onChange={(e) => setTest((p) => ({ ...p, tracking: e.target.value }))} /></div>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => runTest("carrier")}>{t("intg.track")}</button>
                    </div>
                    {test.carrierResult && <pre style={{ fontSize: "11px", background: "var(--surface-2, rgba(127,127,127,0.1))", padding: "10px", borderRadius: "8px", marginTop: "10px", overflowX: "auto" }}>{JSON.stringify(test.carrierResult, null, 2)}</pre>}
                </div>
            ))}

            {card("s3", t("intg.s3"), <>
                {txt("s3", "region", t("intg.region"))}
                {txt("s3", "bucket", t("intg.bucket"))}
                {txt("s3", "access_key_id", t("intg.accessKey"))}
                {txt("s3", "secret_access_key", t("intg.secretKey"), data.s3.config.has_secret_access_key)}
                {status && !status.s3SdkInstalled && <div style={{ fontSize: "12px", color: "#ff9f0a", marginBottom: "8px" }}>{t("intg.s3SdkMissing")}</div>}
            </>)}
        </div>
    );
};

export default Integrations;
